import {
  SiteIntelligenceSummaryToJSON,
  SiteIntelligenceRoutingResponseToJSON,
  SiteIntelligenceRoutingFindingListResponseToJSON,
  SiteIntelligenceRoutingReviewQuestionListResponseToJSON,
  SiteIntelligenceRoutingFindingDetailResponseToJSON,
  SiteIntelligenceTopologyResponseToJSON,
  SiteIntelligenceTopologyFindingListResponseToJSON,
  SiteIntelligenceTopologyReviewQuestionListResponseToJSON,
  SiteIntelligenceTopologyFindingDetailResponseToJSON,
  SiteIntelligenceNamingListResponseToJSON,
  SiteIntelligenceNamingReviewQuestionListResponseToJSON,
  SiteIntelligenceNamingFindingDetailResponseToJSON,
  SiteIntelligenceVlanListResponseToJSON,
  SiteIntelligenceVlanDetailResponseToJSON
} from '@app/types/sdk';
import type {
  SiteIntelligenceNamingFindingDetailResponse,
  SiteIntelligenceNamingListResponse,
  SiteIntelligenceNamingReviewQuestionListResponse,
  SiteIntelligenceRouting,
  SiteIntelligenceRoutingFindingDetailResponse,
  SiteIntelligenceRoutingFindingListResponse,
  SiteIntelligenceRoutingObservation,
  SiteIntelligenceRoutingOperatorEvidence,
  SiteIntelligenceRoutingOperatorMeasure,
  SiteIntelligenceRoutingResponse,
  SiteIntelligenceRoutingReviewQuestionListResponse,
  SiteIntelligenceSnapshot,
  SiteIntelligenceSummary,
  SiteIntelligenceTopologyFindingDetailResponse,
  SiteIntelligenceTopologyFindingListResponse,
  SiteIntelligenceTopologyReviewQuestionListResponse,
  SiteIntelligenceTopologyResponse,
  SiteIntelligenceVlanDetailResponse,
  SiteIntelligenceVlanFinding,
  SiteIntelligenceVlanListResponse
} from '@app/types/sdk';

/** UI-only site identities with Site Intelligence fixture data. */
export const CAMPUS_B_FIREWALL_DEMO_SITE_ID = 'site-campus-b-firewall-demo';

/**
 * This fixture projects the CAMPUSB observed `site_summary.v1` snapshot from
 * 2026-09-07 into the public Inventory Service contract. It intentionally
 * retains public UUIDs for joins, but never exposes internal Site Insights
 * observation, component, or physical-link handles to the renderer.
 */
const snapshot: SiteIntelligenceSnapshot = {
  id: '6bb724a2-20f3-42c6-b4c2-fa5a1387a3cb',
  generatedAt: new Date('2026-09-07T05:33:14.561Z'),
  status: 'available'
};

const devices = {
  distB1: { id: '40d55e2f-5d4e-4377-8bd7-49f1d7ede6da', hostname: 'CAMPUSB-FW-DIST-B1-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  distA2: { id: '5319d0fc-17c9-48d9-989c-6c552486a189', hostname: 'CAMPUSB-FW-DIST-A2-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  distC1: { id: '6482ea3e-f142-4208-8926-50e39d5b19d5', hostname: 'CAMPUSB-FW-DIST-C1-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  core1: { id: '65bf196a-0c76-405a-8a6f-a33bf7b46175', hostname: 'CAMPUSB-FW-CORE1-S02', storedRole: 'Core', deviceType: 'Router', vendor: 'Arista', model: 'cEOSLab' },
  panB: { id: '770f9c9b-f48a-4985-95cc-7ae8a92afbb7', hostname: 'CAMPUSB-FW-PAN-B-S02', storedRole: 'Firewall', deviceType: 'Firewall', vendor: 'Palo Alto', model: 'PA-VM' },
  isp: { id: '7fac56ca-5532-4eb1-a863-47be2419c8d8', hostname: 'CAMPUSB-FW-ISP-S02', storedRole: 'Edge', deviceType: 'Router', vendor: 'Arista', model: 'cEOSLab' },
  accB: { id: '85f8b396-4379-47e9-961c-59be01e422fd', hostname: 'CAMPUSB-FW-ACC-B-S02', storedRole: 'Access', deviceType: 'Switch', vendor: 'Arista', model: 'cEOSLab' },
  accC: { id: '8dab4b6f-dfc8-45d5-975d-87d6f108d515', hostname: 'CAMPUSB-FW-ACC-C-S02', storedRole: 'Access', deviceType: 'Switch', vendor: 'Arista', model: 'cEOSLab' },
  distB2: { id: 'a160bbcc-d637-4873-8369-9e325d2b79b2', hostname: 'CAMPUSB-FW-DIST-B2-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  distC2: { id: 'a672fe4e-88da-4512-bf43-afabee75d9d9', hostname: 'CAMPUSB-FW-DIST-C2-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  accA: { id: 'a8d7031d-cdbd-4c83-8b83-a8bcf1738241', hostname: 'CAMPUSB-FW-ACC-A-S02', storedRole: 'Access', deviceType: 'Switch', vendor: 'Arista', model: 'cEOSLab' },
  core2: { id: 'b81259cf-83bb-4c5b-8ed8-e6ebb4aeb60f', hostname: 'CAMPUSB-FW-CORE2-S02', storedRole: 'Core', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  distA1: { id: 'bd24e430-8c20-411c-a9c5-a9b7c6e11ed7', hostname: 'CAMPUSB-FW-DIST-A1-S02', storedRole: 'Distribution', deviceType: 'Layer 3 Switch', vendor: 'Arista', model: 'cEOSLab' },
  panA: { id: 'be0142f4-fce3-4d92-9f8b-65c0346a38a7', hostname: 'CAMPUSB-FW-PAN-A-S02', storedRole: 'Firewall', deviceType: 'Firewall', vendor: 'Palo Alto', model: 'PA-VM' }
};

type DeviceKey = keyof typeof devices;
type Membership = { deviceId: string; interfaceName: string };

const allDevices = Object.values(devices);
const ids = (...keys: DeviceKey[]) => keys.map((key) => devices[key].id);
const deviceCatalogFor = (deviceIds: string[]) => allDevices.filter((device) => deviceIds.includes(device.id));
const interfaces = (device: DeviceKey, names: string[]): Membership[] => names.map((interfaceName) => ({ deviceId: devices[device].id, interfaceName }));
const uniqueMembers = (members: Membership[]) => [...new Set(members.map((member) => member.deviceId))];
const link = (from: DeviceKey, to: DeviceKey, linkCount = 1) => ({ fromDeviceId: devices[from].id, toDeviceId: devices[to].id, linkCount });

const findingIds = {
  namingSummary: 'a1000000-0000-4000-8000-000000000000',
  namingFamily: 'a1000000-0000-4000-8000-000000000001',
  topologyArchitecture: 'a2000000-0000-4000-8000-000000000001',
  topologySummary: 'a2000000-0000-4000-8000-000000000008',
  topologyAccess: 'a2000000-0000-4000-8000-000000000002',
  topologyCore: 'a2000000-0000-4000-8000-000000000003',
  topologyHighestDegree: 'a2000000-0000-4000-8000-000000000004',
  topologyIsolatedPanB: 'a2000000-0000-4000-8000-000000000005',
  topologyIsolatedIsp: 'a2000000-0000-4000-8000-000000000006',
  topologyIsolatedPanA: 'a2000000-0000-4000-8000-000000000007',
  vlanSummary: 'a3000000-0000-4000-8000-000000000001',
  vlanDefault: 'a3000000-0000-4000-8000-000000000002',
  vlanData: 'a3000000-0000-4000-8000-000000000003',
  vlanVoice: 'a3000000-0000-4000-8000-000000000004',
  vlanManagement: 'a3000000-0000-4000-8000-000000000005',
  vlanKeepalive: 'a3000000-0000-4000-8000-000000000006',
  vlanSharedMembers: 'a3000000-0000-4000-8000-000000000007',
  vlanDataDifference: 'a3000000-0000-4000-8000-000000000008',
  vlanVoiceDifference: 'a3000000-0000-4000-8000-000000000009',
  vlanManagementDifference: 'a3000000-0000-4000-8000-000000000010',
  vlanNamingQuestion: 'a3000000-0000-4000-8000-000000000011',
  routingSummary: 'a4000000-0000-4000-8000-000000000001',
  routingBGP1: 'a4000000-0000-4000-8000-000000000002',
  routingCampus: 'a4000000-0000-4000-8000-000000000003',
  routingDefault: 'a4000000-0000-4000-8000-000000000004',
  routingHypothesis: 'a4000000-0000-4000-8000-000000000005',
  routingCommonality: 'a4000000-0000-4000-8000-000000000006',
  routingTimerQuestion: 'a4000000-0000-4000-8000-000000000007',
  routingVersionQuestion: 'a4000000-0000-4000-8000-000000000008'
};

const accessDeviceIds = ids('accA', 'accB', 'accC');
const coreDeviceIds = ids('core1', 'core2');
const distributionDeviceIds = ids('distA1', 'distA2', 'distB1', 'distB2', 'distC1', 'distC2');
const primaryComponentDeviceIds = ids('distB1', 'distA2', 'distC1', 'core1', 'accB', 'accC', 'distB2', 'distC2', 'accA', 'core2', 'distA1');
const vlanMemberDeviceIds = ids('distB1', 'distA2', 'distC1', 'accB', 'accC', 'distB2', 'distC2', 'accA', 'distA1');

const distributionToCoreLinks = [
  link('distB1', 'core1', 2), link('distB1', 'core2', 2),
  link('distA2', 'core1'), link('distA2', 'core2'),
  link('distC1', 'core1'), link('distC1', 'core2'),
  link('distB2', 'core1'), link('distB2', 'core2'),
  link('distC2', 'core1'), link('distC2', 'core2'),
  link('distA1', 'core1'), link('distA1', 'core2')
];
const accessToDistributionLinks = [
  link('distB1', 'accB'), link('accB', 'distB2'),
  link('distC1', 'accC'), link('accC', 'distC2'),
  link('distA2', 'accA'), link('accA', 'distA1')
];
const coreLink = [link('core1', 'core2')];

const buildingA = ids('distA2', 'accA', 'distA1');
const buildingB = ids('distB1', 'accB', 'distB2');
const buildingC = ids('distC1', 'accC', 'distC2');

const vlan1Members = [
  ...interfaces('distB1', ['Po47']), ...interfaces('distA2', ['Cpu', 'Po1', 'Po47']), ...interfaces('distC1', ['Po47']),
  ...interfaces('accC', ['Et1', 'Et2', 'Et3', 'Po49']), ...interfaces('distB2', ['Po47']), ...interfaces('distC2', ['Po1', 'Po47']), ...interfaces('distA1', ['Po47'])
];
const vlan20Members = [
  ...interfaces('distB1', ['Po1', 'Po2', 'Po47']), ...interfaces('distA2', ['Cpu', 'Po1', 'Po47']), ...interfaces('distC1', ['Po1', 'Po2', 'Po47']),
  ...interfaces('accB', ['Et1', 'Et3', 'Et4', 'Po49']), ...interfaces('accC', ['Et1', 'Et3', 'Po49']), ...interfaces('distB2', ['Po1', 'Po2', 'Po47']),
  ...interfaces('distC2', ['Po1', 'Po47']), ...interfaces('accA', ['Et1', 'Et3', 'Et4', 'Po49']), ...interfaces('distA1', ['Et5', 'Po1', 'Po2', 'Po47'])
];
const vlan30Members = [
  ...interfaces('distB1', ['Po1', 'Po2', 'Po47']), ...interfaces('distA2', ['Cpu', 'Po1', 'Po47']), ...interfaces('distC1', ['Po1', 'Po2', 'Po47']),
  ...interfaces('accB', ['Po49']), ...interfaces('accC', ['Po49']), ...interfaces('distB2', ['Po1', 'Po2', 'Po47']),
  ...interfaces('distC2', ['Po1', 'Po47']), ...interfaces('accA', ['Po49']), ...interfaces('distA1', ['Po1', 'Po2', 'Po47'])
];
const vlan40Members = [
  ...interfaces('distB1', ['Po1', 'Po2', 'Po47']), ...interfaces('distA2', ['Cpu', 'Po1', 'Po47']), ...interfaces('distC1', ['Po1', 'Po2', 'Po47']),
  ...interfaces('accB', ['Et2', 'Po49']), ...interfaces('accC', ['Et2', 'Po49']), ...interfaces('distB2', ['Po1', 'Po2', 'Po47']),
  ...interfaces('distC2', ['Po1', 'Po47']), ...interfaces('accA', ['Et2', 'Po49']), ...interfaces('distA1', ['Po1', 'Po2', 'Po47'])
];
const vlan4094Members = [
  ...interfaces('distB1', ['Po47']), ...interfaces('distA2', ['Cpu', 'Po47']), ...interfaces('distC1', ['Po47']),
  ...interfaces('distB2', ['Po47']), ...interfaces('distC2', ['Po47']), ...interfaces('distA1', ['Po47'])
];

const purpose = (findingId: string, text: string, confidence: 'medium' | 'high', deviceIds: string[], vlanIds: number[]): SiteIntelligenceVlanFinding => ({ findingId, text, confidence, deviceIds: new Set(deviceIds), vlanIds: new Set(vlanIds) });
const buildingNames = (suffix: string) => [
  { name: `BLDG-A-${suffix}`, deviceIds: new Set(buildingA) }, { name: `BLDG-B-${suffix}`, deviceIds: new Set(buildingB) }, { name: `BLDG-C-${suffix}`, deviceIds: new Set(buildingC) }
];

const vlanDefinitions = [
  {
    vlanId: 1, nameStatus: 'agreed' as const, observedNames: new Set(['default']), nameObservations: [{ name: 'default', deviceIds: new Set(ids('distB1', 'distA2', 'distC1', 'accC', 'distB2', 'distC2', 'distA1')) }],
    members: vlan1Members, memberDeviceCount: 7, namedMemberDeviceCount: 7,
    purpose: purpose(findingIds.vlanDefault, 'The observed agreed name indicates the VLAN is the default/native VLAN.', 'high', uniqueMembers(vlan1Members), [1])
  },
  {
    vlanId: 20, nameStatus: 'conflicting' as const, observedNames: new Set(['BLDG-A-DATA', 'BLDG-B-DATA', 'BLDG-C-DATA']), nameObservations: buildingNames('DATA'),
    members: vlan20Members, memberDeviceCount: 9, namedMemberDeviceCount: 9,
    purpose: purpose(findingIds.vlanData, 'The observed conflicting building-specific names collectively suggest a data-user segment split by building.', 'medium', vlanMemberDeviceIds, [20])
  },
  {
    vlanId: 30, nameStatus: 'conflicting' as const, observedNames: new Set(['BLDG-A-VOICE', 'BLDG-B-VOICE', 'BLDG-C-VOICE']), nameObservations: buildingNames('VOICE'),
    members: vlan30Members, memberDeviceCount: 9, namedMemberDeviceCount: 9,
    purpose: purpose(findingIds.vlanVoice, 'The observed conflicting building-specific names collectively suggest a voice-user segment split by building.', 'medium', vlanMemberDeviceIds, [30])
  },
  {
    vlanId: 40, nameStatus: 'conflicting' as const, observedNames: new Set(['BLDG-A-MGMT', 'BLDG-B-MGMT', 'BLDG-C-MGMT']), nameObservations: buildingNames('MGMT'),
    members: vlan40Members, memberDeviceCount: 9, namedMemberDeviceCount: 9,
    purpose: purpose(findingIds.vlanManagement, 'The observed conflicting building-specific names collectively suggest a management segment split by building.', 'medium', vlanMemberDeviceIds, [40])
  },
  {
    vlanId: 4094, nameStatus: 'agreed' as const, observedNames: new Set(['MLAG-KEEPALIVE']), nameObservations: [{ name: 'MLAG-KEEPALIVE', deviceIds: new Set(ids('distB1', 'distA2', 'distC1', 'distB2', 'distC2', 'distA1')) }],
    members: vlan4094Members, memberDeviceCount: 6, namedMemberDeviceCount: 6,
    purpose: purpose(findingIds.vlanKeepalive, 'The observed agreed name indicates the VLAN is used for MLAG keepalive.', 'high', uniqueMembers(vlan4094Members), [4094])
  }
];

const sharedMemberFinding = purpose(findingIds.vlanSharedMembers, 'Three observed VLANs share the same resolved member-device set.', 'high', vlanMemberDeviceIds, [20, 30, 40]);
const differenceFor = (vlanId: 20 | 30 | 40, suffix: string, findingId: string) => purpose(findingId, `Resolved member devices reported different current names for the VLAN that appear as building-specific ${suffix.toLowerCase()} names.`, 'high', vlanMemberDeviceIds, [vlanId]);
const reviewQuestion = purpose(findingIds.vlanNamingQuestion, 'Do the building-specific names correspond to intentionally distinct VLANs for each building, or are they different local names applied to the same site-wide VLAN membership?', 'medium', vlanMemberDeviceIds, [20, 30, 40]);

// The public routing fixture mirrors the CAMPUSB BGP snapshot used during the
// routing-adjacency implementation review. Evidence remains typed aggregate
// observations; raw ASN/IP/session data and internal observation handles are
// deliberately private to Site Insights and Topology Service.
const routing: SiteIntelligenceRouting = {
  state: 'available',
  protocol: 'bgp',
  resolvedAt: new Date('2026-09-11T09:32:49.000Z'),
  instanceCount: 3,
  resolvedAdjacencyCount: 20,
  summary: { findingId: findingIds.routingSummary, category: 'observed_fact', confidence: 'high', text: 'At the selected snapshot, 20 in-site BGP links were resolved across three instances. All observed resolved links report eBGP and IPv4-unicast session patterns with distinct local and remote ASN pairs. The bgp:1 and bgp:default instances contain resolved links, while bgp:CAMPUS-VR has two visible devices and no resolved in-site link.' },
  instancePreview: [
    { instance: 'bgp:1', visibleDeviceCount: 8, resolvedAdjacencyCount: 9, summary: { findingId: findingIds.routingBGP1, category: 'observed_fact', confidence: 'high', text: 'The bgp:1 instance has eight visible devices and nine resolved in-site BGP links. Its resolved links report eBGP, IPv4 unicast, and distinct local and remote ASN pairs; timer and negotiated-version rollups have incomplete coverage for several links.' } },
    { instance: 'bgp:CAMPUS-VR', visibleDeviceCount: 2, resolvedAdjacencyCount: 0, summary: { findingId: findingIds.routingCampus, category: 'observed_fact', confidence: 'high', text: 'The bgp:CAMPUS-VR instance has two visible firewall devices and no resolved in-site BGP links at the snapshot time.' } },
    { instance: 'bgp:default', visibleDeviceCount: 9, resolvedAdjacencyCount: 11, summary: { findingId: findingIds.routingDefault, category: 'observed_fact', confidence: 'high', text: 'The bgp:default instance has nine visible devices and eleven resolved in-site BGP links. Its resolved links report eBGP, IPv4 unicast, distinct local and remote ASN pairs, and a uniform negotiated BGP version rollup of 4.' } }
  ],
  interpretationCount: 1,
  commonalityCount: 1,
  observedDifferenceCount: 0,
  reviewQuestionCount: 2,
  interpretationPreview: [{ findingId: findingIds.routingHypothesis, category: 'design_hypothesis', confidence: 'medium', text: 'The repeated dual-homing patterns in bgp:1 and bgp:default are consistent with redundant two-peer attachments for the observed distribution-device cohorts.' }],
  commonalityPreview: [{ findingId: findingIds.routingCommonality, category: 'observed_fact', confidence: 'high', text: 'Core and distribution stored roles are present in both resolved BGP instances, while the empty CAMPUS-VR instance contains firewall-role devices.' }],
  observedDifferencePreview: [],
  reviewQuestionPreview: [
    { findingId: findingIds.routingTimerQuestion, text: 'Which resolved links are outside the reported hold and keepalive timer-rollup coverage?' }
  ]
};

const routingObservations: Record<'fabric' | 'ebgp' | 'ipv4' | 'pairedAsn' | 'timerCoverage' | 'versionCoverage' | 'bgp1Coverage' | 'bgp1Session' | 'bgp1TimerCoverage' | 'campusNoAdjacency' | 'campusRole' | 'defaultCoverage' | 'defaultSession' | 'defaultVersion' | 'dualHomingBGP1' | 'dualHomingDefault' | 'rolesBGP1' | 'rolesDefault', SiteIntelligenceRoutingObservation> = {
  fabric: {
    kind: 'resolved_bgp_fabric', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(),
    observedCount: 20, adjacencyCount: 20
  },
  ebgp: {
    kind: 'session_type_pattern', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['ebgp']),
    observedCount: 20, adjacencyCount: 20
  },
  ipv4: {
    kind: 'address_family_pattern', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['ipv4_unicast']),
    observedCount: 20, adjacencyCount: 20
  },
  pairedAsn: {
    kind: 'paired_bgp_asn_relationship_pattern', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['distinct_local_remote_as']),
    eligibleCount: 20, observedCount: 20, matchingCount: 20, adjacencyCount: 20
  },
  timerCoverage: {
    kind: 'bgp_hold_time_rollup_coverage_incomplete', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(),
    eligibleCount: 20, observedCount: 11, exceptionCount: 9, adjacencyCount: 20
  },
  versionCoverage: {
    kind: 'bgp_negotiated_version_rollup_coverage_incomplete', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(),
    eligibleCount: 20, observedCount: 13, exceptionCount: 7, adjacencyCount: 20
  },
  bgp1Coverage: {
    kind: 'instance_resolved_adjacency_coverage', maxConfidence: 'high', deviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds]), peerDeviceIds: new Set(), values: new Set(),
    observedCount: 8, adjacencyCount: 9, instance: 'bgp:1'
  },
  bgp1Session: {
    kind: 'instance_session_type_pattern', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['ebgp']),
    observedCount: 9, adjacencyCount: 9, instance: 'bgp:1'
  },
  bgp1TimerCoverage: {
    kind: 'instance_bgp_hold_time_rollup_coverage_incomplete', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(),
    eligibleCount: 9, observedCount: 2, exceptionCount: 7, adjacencyCount: 9, instance: 'bgp:1'
  },
  campusNoAdjacency: {
    kind: 'instance_without_resolved_adjacency', maxConfidence: 'high', deviceIds: new Set(ids('panA', 'panB')), peerDeviceIds: new Set(), values: new Set(),
    eligibleCount: 0, observedCount: 2, matchingCount: 0, exceptionCount: 0, adjacencyCount: 0, instance: 'bgp:CAMPUS-VR'
  },
  campusRole: {
    kind: 'shared_stored_role', maxConfidence: 'high', deviceIds: new Set(ids('panA', 'panB')), peerDeviceIds: new Set(), role: 'firewall', values: new Set(),
    eligibleCount: 2, observedCount: 2, matchingCount: 2, instance: 'bgp:CAMPUS-VR'
  },
  defaultCoverage: {
    kind: 'instance_resolved_adjacency_coverage', maxConfidence: 'high', deviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds, ...ids('isp')]), peerDeviceIds: new Set(), values: new Set(),
    observedCount: 9, adjacencyCount: 11, instance: 'bgp:default'
  },
  defaultSession: {
    kind: 'instance_session_type_pattern', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['ebgp']),
    observedCount: 11, adjacencyCount: 11, instance: 'bgp:default'
  },
  defaultVersion: {
    kind: 'instance_uniform_bgp_negotiated_version_rollup', maxConfidence: 'high', deviceIds: new Set(), peerDeviceIds: new Set(), values: new Set(['4']),
    eligibleCount: 11, observedCount: 11, matchingCount: 11, adjacencyCount: 11, instance: 'bgp:default'
  },
  dualHomingBGP1: {
    kind: 'repeated_dual_homing', maxConfidence: 'high', deviceIds: new Set(ids('distC2', 'distA1')), peerDeviceIds: new Set(coreDeviceIds), role: 'distribution', values: new Set(),
    eligibleCount: 2, observedCount: 2, matchingCount: 2, instance: 'bgp:1'
  },
  dualHomingDefault: {
    kind: 'repeated_dual_homing', maxConfidence: 'high', deviceIds: new Set(ids('distB1', 'distA2', 'distC1', 'distB2')), peerDeviceIds: new Set(coreDeviceIds), role: 'distribution', values: new Set(),
    eligibleCount: 4, observedCount: 4, matchingCount: 4, instance: 'bgp:default'
  },
  rolesBGP1: {
    kind: 'shared_stored_role', maxConfidence: 'high', deviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds]), peerDeviceIds: new Set(), role: 'core and distribution', values: new Set(),
    eligibleCount: 8, observedCount: 8, matchingCount: 8, instance: 'bgp:1'
  },
  rolesDefault: {
    kind: 'shared_stored_role', maxConfidence: 'high', deviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds]), peerDeviceIds: new Set(), role: 'core and distribution', values: new Set(),
    eligibleCount: 8, observedCount: 8, matchingCount: 8, instance: 'bgp:default'
  }
};

type RoutingOperatorMeasureSpec = {
  key: SiteIntelligenceRoutingOperatorMeasure['key'];
  label: string;
  value: number | undefined;
};

/** Mirrors the public operator-evidence projection for the BGP fixture. */
const routingOperatorEvidenceFor = (observation: SiteIntelligenceRoutingObservation): SiteIntelligenceRoutingOperatorEvidence | null => {
  const build = (
    type: SiteIntelligenceRoutingOperatorEvidence['type'],
    title: string,
    status: SiteIntelligenceRoutingOperatorEvidence['status'],
    measures: RoutingOperatorMeasureSpec[],
    metric?: string
  ): SiteIntelligenceRoutingOperatorEvidence => ({
    type,
    title,
    status,
    ...(observation.instance ? { instance: observation.instance } : {}),
    ...(observation.role ? { role: observation.role } : {}),
    ...(metric ? { metric } : {}),
    values: new Set(observation.values),
    measures: measures.flatMap((measure) => measure.value === undefined ? [] : [{ key: measure.key, label: measure.label, value: measure.value }]),
    deviceIds: new Set(observation.deviceIds),
    peerDeviceIds: new Set(observation.peerDeviceIds)
  });
  const links = (): RoutingOperatorMeasureSpec => ({ key: 'resolved_bgp_links', label: 'Resolved BGP links', value: observation.adjacencyCount });
  const kind = observation.kind.replace(/^instance_/, '');

  switch (kind) {
    case 'resolved_bgp_fabric':
      return build('fabric', 'Resolved BGP fabric', 'complete', [links()]);
    case 'resolved_adjacency_coverage':
      return build('instance_coverage', 'BGP instance coverage', 'complete', [
        { key: 'visible_bgp_devices', label: 'Devices in BGP instance', value: observation.observedCount }, links()
      ]);
    case 'without_resolved_adjacency':
      return build('instance_coverage', 'BGP instance coverage', 'informational', [
        { key: 'visible_bgp_devices', label: 'Devices in BGP instance', value: observation.observedCount }, links()
      ]);
    case 'session_type_pattern':
      return build('session_type', 'BGP session-type coverage', 'complete', [
        links(), { key: 'links_with_session_data', label: 'Links with session-type data', value: observation.observedCount }
      ]);
    case 'address_family_pattern':
      return build('address_family', 'BGP address-family coverage', 'complete', [
        links(), { key: 'links_with_session_data', label: 'Links with address-family data', value: observation.observedCount }
      ]);
    case 'paired_bgp_asn_relationship_pattern':
      return build('asn_pairing', 'Paired BGP ASN relationship', 'complete', [
        links(),
        { key: 'session_pairs_evaluated', label: 'Represented session pairs', value: observation.eligibleCount },
        { key: 'session_pairs_with_complete_asn_data', label: 'Session pairs with complete ASN data', value: observation.observedCount },
        { key: 'matching_session_pairs', label: 'Session pairs matching the relationship', value: observation.matchingCount }
      ]);
    case 'bgp_hold_time_rollup_coverage_incomplete':
      return build('timer', 'BGP hold-timer rollup', 'incomplete', [
        links(),
        { key: 'links_with_metric_data', label: 'Links with hold-timer data', value: observation.observedCount },
        { key: 'links_missing_metric_data', label: 'Links missing hold-timer data', value: observation.exceptionCount }
      ], 'hold_time_seconds');
    case 'bgp_negotiated_version_rollup_coverage_incomplete':
      return build('negotiated_version', 'BGP negotiated-version rollup', 'incomplete', [
        links(),
        { key: 'links_with_metric_data', label: 'Links with negotiated-version data', value: observation.observedCount },
        { key: 'links_missing_metric_data', label: 'Links missing negotiated-version data', value: observation.exceptionCount }
      ], 'negotiated_version');
    case 'uniform_bgp_negotiated_version_rollup':
      return build('negotiated_version', 'BGP negotiated-version rollup', 'complete', [
        links(), { key: 'links_with_metric_data', label: 'Links with negotiated-version data', value: observation.observedCount }
      ], 'negotiated_version');
    case 'repeated_dual_homing':
      return build('dual_homing', 'Repeated dual-homing pattern', 'complete', [
        { key: 'devices_evaluated', label: 'Devices evaluated', value: observation.eligibleCount },
        { key: 'devices_with_two_resolved_peers', label: 'Devices with two resolved BGP peers', value: observation.observedCount },
        { key: 'peer_devices', label: 'Shared peer devices', value: observation.peerDeviceIds.size }
      ]);
    case 'shared_stored_role':
      return build('stored_role', 'Shared stored device role', 'complete', [
        { key: 'devices_evaluated', label: 'Devices evaluated', value: observation.eligibleCount },
        { key: 'devices_with_stored_role', label: 'Devices with this stored role', value: observation.observedCount }
      ]);
    default:
      return null;
  }
};

const routingOperatorEvidence = (...observations: SiteIntelligenceRoutingObservation[]) => observations.flatMap((observation) => {
  const evidence = routingOperatorEvidenceFor(observation);
  return evidence ? [evidence] : [];
});

const summary: SiteIntelligenceSummary = {
  snapshot,
  coverage: { deviceCount: 14 },
  sections: {
    naming: {
      state: 'available', familyCount: 1, variantCount: 4, reviewQuestionCount: 0,
      summary: { findingId: findingIds.namingSummary, text: 'All hostnames share the CAMPUSB-FW prefix and S02 suffix; the middle name token varies by observed function and instance.' },
      familyPreview: [{ findingId: findingIds.namingFamily, pattern: 'CAMPUSB-FW-<name>-S02', interpretation: 'The shared prefix and suffix bound a middle name token whose form varies by observed device function and instance.', confidence: 'medium', supportCount: 14, variantCount: 4 }],
      reviewQuestionPreview: []
    },
    topology: {
      state: 'available',
      observed: { inventoryDeviceCount: 14, matchedDeviceCount: 14, physicalAdjacencyCount: 22, physicalConnectionCount: 31, haPairCount: 0, componentCount: 4, highestPhysicalDegree: 7, highestDegreeDeviceCount: 2, isolatedDeviceCount: 3, multiLinkAdjacencyCount: 5 },
      architecture: { findingId: findingIds.topologyArchitecture, bestFit: 'three_tier', confidence: 'high' },
      summary: { findingId: findingIds.topologySummary, text: 'Observed topology shows a three-tier structure: distribution devices directly attach to core devices and to access devices; two core devices form the highest-degree group.' },
      findingCount: 6, reviewQuestionCount: 0,
      findingPreview: [
        { findingId: findingIds.topologyAccess, text: 'Three access-role devices are directly attached to six distribution-role devices.', confidence: 'high' },
        { findingId: findingIds.topologyCore, text: 'Six distribution-role devices are directly attached to two core-role devices.', confidence: 'high' },
        { findingId: findingIds.topologyHighestDegree, text: 'Two core devices form the highest-degree group in the observed topology.', confidence: 'high' }
      ],
      reviewQuestionPreview: []
    },
    vlans: {
      state: 'available', vlanCount: 5, memberDeviceCount: 9, memberInterfaceCount: 92,
      summary: { findingId: findingIds.vlanSummary, text: 'The catalog includes an agreed default VLAN and MLAG keepalive VLAN. Three other VLANs show building-specific names for data, voice, and management with the same resolved member-device set.' }
    },
    routing
  }
};

const naming: SiteIntelligenceNamingListResponse = {
  snapshot, state: 'available', familyCount: 1, variantCount: 4, reviewQuestionCount: 0, summary: summary.sections.naming.summary,
  items: summary.sections.naming.familyPreview ?? [], meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 1 }
};
const topology: SiteIntelligenceTopologyResponse = { snapshot, topology: summary.sections.topology };
const topologyFindings: SiteIntelligenceTopologyFindingListResponse = {
  snapshot, state: 'available', findingCount: 6,
  items: [
    ...(summary.sections.topology.findingPreview ?? []),
    { findingId: findingIds.topologyIsolatedPanB, text: 'This device has no observed physical adjacencies.', confidence: 'high' },
    { findingId: findingIds.topologyIsolatedIsp, text: 'This device has no observed physical adjacencies.', confidence: 'high' },
    { findingId: findingIds.topologyIsolatedPanA, text: 'This device has no observed physical adjacencies.', confidence: 'high' }
  ],
  meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 6 }
};
const namingReviewQuestions: SiteIntelligenceNamingReviewQuestionListResponse = {
  snapshot, state: 'available', reviewQuestionCount: 0, items: [], meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 0 }
};
const topologyReviewQuestions: SiteIntelligenceTopologyReviewQuestionListResponse = {
  snapshot, state: 'available', reviewQuestionCount: 0, items: [], meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 0 }
};
const routingOverview: SiteIntelligenceRoutingResponse = { snapshot, routing };
const routingFindings: SiteIntelligenceRoutingFindingListResponse = {
  snapshot,
  state: 'available',
  findingCount: 6,
  items: [
    { findingId: findingIds.routingSummary, kind: 'summary', category: 'observed_fact', confidence: 'high', text: routing.summary!.text },
    { findingId: findingIds.routingBGP1, kind: 'instance_summary', instance: 'bgp:1', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![0].summary!.text },
    { findingId: findingIds.routingCampus, kind: 'instance_summary', instance: 'bgp:CAMPUS-VR', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![1].summary!.text },
    { findingId: findingIds.routingDefault, kind: 'instance_summary', instance: 'bgp:default', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![2].summary!.text },
    { findingId: findingIds.routingHypothesis, kind: 'interpretation', category: 'design_hypothesis', confidence: 'medium', text: routing.interpretationPreview![0].text },
    { findingId: findingIds.routingCommonality, kind: 'commonality', category: 'observed_fact', confidence: 'high', text: routing.commonalityPreview![0].text }
  ],
  meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 6 }
};
const routingReviewQuestions: SiteIntelligenceRoutingReviewQuestionListResponse = {
  snapshot,
  state: 'available',
  reviewQuestionCount: 2,
  items: [
    { findingId: findingIds.routingTimerQuestion, text: routing.reviewQuestionPreview![0].text },
    { findingId: findingIds.routingVersionQuestion, text: 'Which resolved links are outside the negotiated-version rollup coverage for bgp:1 and the selected snapshot?' }
  ],
  meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 2 }
};
const routingDetails: Record<string, SiteIntelligenceRoutingFindingDetailResponse> = {
  [findingIds.routingSummary]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds, ...ids('isp', 'panA', 'panB')]),
    finding: { findingId: findingIds.routingSummary, kind: 'summary', category: 'observed_fact', confidence: 'high', text: routing.summary!.text, evidence: { observations: [routingObservations.fabric, routingObservations.ebgp, routingObservations.ipv4, routingObservations.pairedAsn, routingObservations.timerCoverage, routingObservations.versionCoverage], operatorEvidence: routingOperatorEvidence(routingObservations.fabric, routingObservations.ebgp, routingObservations.ipv4, routingObservations.pairedAsn, routingObservations.timerCoverage, routingObservations.versionCoverage) } }
  },
  [findingIds.routingBGP1]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.routingBGP1, kind: 'instance_summary', instance: 'bgp:1', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![0].summary!.text, evidence: { observations: [routingObservations.bgp1Coverage, routingObservations.bgp1Session, routingObservations.bgp1TimerCoverage, routingObservations.pairedAsn], operatorEvidence: routingOperatorEvidence(routingObservations.bgp1Coverage, routingObservations.bgp1Session, routingObservations.bgp1TimerCoverage, routingObservations.pairedAsn) } }
  },
  [findingIds.routingDefault]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds, ...ids('isp')]),
    finding: { findingId: findingIds.routingDefault, kind: 'instance_summary', instance: 'bgp:default', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![2].summary!.text, evidence: { observations: [routingObservations.defaultCoverage, routingObservations.defaultSession, routingObservations.defaultVersion, routingObservations.pairedAsn], operatorEvidence: routingOperatorEvidence(routingObservations.defaultCoverage, routingObservations.defaultSession, routingObservations.defaultVersion, routingObservations.pairedAsn) } }
  },
  [findingIds.routingCampus]: {
    snapshot, devices: deviceCatalogFor(ids('panA', 'panB')),
    finding: { findingId: findingIds.routingCampus, kind: 'instance_summary', instance: 'bgp:CAMPUS-VR', category: 'observed_fact', confidence: 'high', text: routing.instancePreview![1].summary!.text, evidence: { observations: [routingObservations.campusNoAdjacency, routingObservations.campusRole], operatorEvidence: routingOperatorEvidence(routingObservations.campusNoAdjacency, routingObservations.campusRole) } }
  },
  [findingIds.routingHypothesis]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.routingHypothesis, kind: 'interpretation', category: 'design_hypothesis', confidence: 'medium', text: routing.interpretationPreview![0].text, evidence: { observations: [routingObservations.dualHomingBGP1, routingObservations.dualHomingDefault], operatorEvidence: routingOperatorEvidence(routingObservations.dualHomingBGP1, routingObservations.dualHomingDefault) } }
  },
  [findingIds.routingCommonality]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds, ...ids('panA', 'panB')]),
    finding: { findingId: findingIds.routingCommonality, kind: 'commonality', category: 'observed_fact', confidence: 'high', text: routing.commonalityPreview![0].text, evidence: { observations: [routingObservations.rolesBGP1, routingObservations.rolesDefault, routingObservations.campusRole], operatorEvidence: routingOperatorEvidence(routingObservations.rolesBGP1, routingObservations.rolesDefault, routingObservations.campusRole) } }
  },
  [findingIds.routingTimerQuestion]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.routingTimerQuestion, kind: 'review_question', text: routing.reviewQuestionPreview![0].text, evidence: { observations: [routingObservations.timerCoverage, routingObservations.bgp1TimerCoverage], operatorEvidence: routingOperatorEvidence(routingObservations.timerCoverage, routingObservations.bgp1TimerCoverage) } }
  },
  [findingIds.routingVersionQuestion]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.routingVersionQuestion, kind: 'review_question', text: routingReviewQuestions.items[1].text, evidence: { observations: [routingObservations.versionCoverage], operatorEvidence: routingOperatorEvidence(routingObservations.versionCoverage) } }
  }
};
const vlans: SiteIntelligenceVlanListResponse = {
  snapshot, state: 'available', vlanCount: 5, memberDeviceCount: 9, memberInterfaceCount: 92, summary: summary.sections.vlans.summary,
  items: vlanDefinitions.map((vlan) => ({ vlanId: vlan.vlanId, nameStatus: vlan.nameStatus, observedNames: vlan.observedNames, namedMemberDeviceCount: vlan.namedMemberDeviceCount, memberDeviceCount: vlan.memberDeviceCount, purposePreview: vlan.purpose })),
  meta: { limit: 50, next: undefined, prev: undefined, exactTotal: 5 }
};

const namingDetails: Record<string, SiteIntelligenceNamingFindingDetailResponse> = {
  [findingIds.namingSummary]: {
    snapshot, devices: allDevices,
    finding: {
      findingId: findingIds.namingSummary, text: 'All hostnames share the CAMPUSB-FW prefix and S02 suffix; the middle name token varies by observed function and instance.', deviceIds: new Set(allDevices.map((device) => device.id)),
      evidence: {
        supportingDeviceIds: new Set(allDevices.map((device) => device.id)),
        families: [{
          pattern: 'CAMPUSB-FW-<name>-S02', deviceIds: new Set(allDevices.map((device) => device.id)),
          variants: [
            { pattern: 'CAMPUSB-FW-ACC-<instance>-S02', interpretation: 'The ACC token corresponds to the observed access-device group.', deviceIds: new Set(accessDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-CORE<digit>-S02', interpretation: 'The CORE token corresponds to the observed core-device group.', deviceIds: new Set(coreDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-DIST-<instance>-S02', interpretation: 'The DIST token corresponds to the observed distribution-device group.', deviceIds: new Set(distributionDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-PAN-<instance>-S02', interpretation: 'The PAN token occurs on the observed firewall device pair.', deviceIds: new Set(ids('panA', 'panB')), explainedBy: new Set(['observed hostname token']) }
          ]
        }]
      }
    }
  },
  [findingIds.namingFamily]: {
    snapshot, devices: allDevices,
    finding: {
      findingId: findingIds.namingFamily, text: 'All hostnames share a CAMPUSB-FW prefix and S02 suffix, while the middle portion encodes an observed function and instance pattern.', confidence: 'medium', deviceIds: new Set(allDevices.map((device) => device.id)),
      evidence: {
        supportingDeviceIds: new Set(allDevices.map((device) => device.id)),
        families: [{
          pattern: 'CAMPUSB-FW-<name>-S02', deviceIds: new Set(allDevices.map((device) => device.id)),
          variants: [
            { pattern: 'CAMPUSB-FW-ACC-<instance>-S02', interpretation: 'The ACC token corresponds to the observed access-device group.', deviceIds: new Set(accessDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-CORE<digit>-S02', interpretation: 'The CORE token corresponds to the observed core-device group.', deviceIds: new Set(coreDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-DIST-<instance>-S02', interpretation: 'The DIST token corresponds to the observed distribution-device group.', deviceIds: new Set(distributionDeviceIds), explainedBy: new Set(['stored role']) },
            { pattern: 'CAMPUSB-FW-PAN-<instance>-S02', interpretation: 'The PAN token occurs on the observed firewall device pair.', deviceIds: new Set(ids('panA', 'panB')), explainedBy: new Set(['observed hostname token']) }
          ]
        }]
      }
    }
  }
};

const primaryComponent = { deviceIds: new Set(primaryComponentDeviceIds) };
const topologyDetails: Record<string, SiteIntelligenceTopologyFindingDetailResponse> = {
  [findingIds.topologyArchitecture]: {
    snapshot, devices: deviceCatalogFor(primaryComponentDeviceIds),
    finding: { findingId: findingIds.topologyArchitecture, text: 'three_tier', confidence: 'high', subjectDeviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds, ...accessDeviceIds]), evidence: { physicalLinks: [...distributionToCoreLinks, ...accessToDistributionLinks], haPairs: [], components: [primaryComponent] } }
  },
  [findingIds.topologySummary]: {
    snapshot, devices: deviceCatalogFor(primaryComponentDeviceIds),
    finding: { findingId: findingIds.topologySummary, text: 'Observed topology shows a three-tier structure: distribution devices directly attach to core devices and to access devices; two core devices form the highest-degree group.', subjectDeviceIds: new Set([...coreDeviceIds, ...distributionDeviceIds, ...accessDeviceIds]), evidence: { physicalLinks: [...distributionToCoreLinks, ...accessToDistributionLinks], haPairs: [], components: [primaryComponent] } }
  },
  [findingIds.topologyAccess]: {
    snapshot, devices: deviceCatalogFor([...accessDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.topologyAccess, text: 'Three access-role devices are directly attached to six distribution-role devices.', confidence: 'high', subjectDeviceIds: new Set(accessDeviceIds), evidence: { physicalLinks: accessToDistributionLinks, haPairs: [], components: [primaryComponent] } }
  },
  [findingIds.topologyCore]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.topologyCore, text: 'Six distribution-role devices are directly attached to two core-role devices.', confidence: 'high', subjectDeviceIds: new Set(distributionDeviceIds), evidence: { physicalLinks: distributionToCoreLinks, haPairs: [], components: [primaryComponent] } }
  },
  [findingIds.topologyHighestDegree]: {
    snapshot, devices: deviceCatalogFor([...coreDeviceIds, ...distributionDeviceIds]),
    finding: { findingId: findingIds.topologyHighestDegree, text: 'Two core devices form the highest-degree group in the observed topology.', confidence: 'high', subjectDeviceIds: new Set(coreDeviceIds), evidence: { physicalLinks: coreLink, haPairs: [], components: [primaryComponent] } }
  },
  [findingIds.topologyIsolatedPanB]: {
    snapshot, devices: [devices.panB],
    finding: { findingId: findingIds.topologyIsolatedPanB, text: 'This device has no observed physical adjacencies.', confidence: 'high', subjectDeviceIds: new Set(ids('panB')), evidence: { physicalLinks: [], haPairs: [], components: [{ deviceIds: new Set(ids('panB')) }] } }
  },
  [findingIds.topologyIsolatedIsp]: {
    snapshot, devices: [devices.isp],
    finding: { findingId: findingIds.topologyIsolatedIsp, text: 'This device has no observed physical adjacencies.', confidence: 'high', subjectDeviceIds: new Set(ids('isp')), evidence: { physicalLinks: [], haPairs: [], components: [{ deviceIds: new Set(ids('isp')) }] } }
  },
  [findingIds.topologyIsolatedPanA]: {
    snapshot, devices: [devices.panA],
    finding: { findingId: findingIds.topologyIsolatedPanA, text: 'This device has no observed physical adjacencies.', confidence: 'high', subjectDeviceIds: new Set(ids('panA')), evidence: { physicalLinks: [], haPairs: [], components: [{ deviceIds: new Set(ids('panA')) }] } }
  }
};

const purposeDifference = (vlanId: 20 | 30 | 40) => vlanId === 20
  ? differenceFor(20, 'data', findingIds.vlanDataDifference)
  : vlanId === 30
    ? differenceFor(30, 'voice', findingIds.vlanVoiceDifference)
    : differenceFor(40, 'management', findingIds.vlanManagementDifference);

const vlanDetails: Record<number, SiteIntelligenceVlanDetailResponse> = Object.fromEntries(vlanDefinitions.map((vlan) => {
  const memberDeviceIds = uniqueMembers(vlan.members);
  const includesBuildingNames = vlan.vlanId === 20 || vlan.vlanId === 30 || vlan.vlanId === 40;
  return [vlan.vlanId, {
    snapshot, devices: deviceCatalogFor(memberDeviceIds),
    vlan: { vlanId: vlan.vlanId, nameStatus: vlan.nameStatus, observedNames: vlan.observedNames, nameObservations: vlan.nameObservations, memberDeviceIds: new Set(memberDeviceIds), members: vlan.members, namedMemberDeviceCount: vlan.namedMemberDeviceCount, memberDeviceCount: vlan.memberDeviceCount },
    purposes: [vlan.purpose],
    commonalities: includesBuildingNames ? [sharedMemberFinding] : [],
    observedDifferences: includesBuildingNames ? [purposeDifference(vlan.vlanId as 20 | 30 | 40)] : [],
    reviewQuestions: includesBuildingNames ? [reviewQuestion] : []
  } satisfies SiteIntelligenceVlanDetailResponse];
}));

const clone = <T,>(value: T): T => structuredClone(value);
const supportedSite = (siteId: string) => siteId === CAMPUS_B_FIREWALL_DEMO_SITE_ID;

/** Returns copies of the inventory identities referenced by Site Intelligence evidence. */
export const listSiteIntelligenceMockDevices = () => clone(allDevices);

/** Returns the compact public landing response for a mock Site Intelligence snapshot. */
export const createSiteIntelligenceMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceSummaryToJSON(clone(summary)) : null;
/** Returns the bounded public BGP routing overview for the mock snapshot. */
export const createSiteIntelligenceRoutingMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceRoutingResponseToJSON(clone(routingOverview)) : null;
/** Returns every mock routing summary, interpretation, commonality, and difference statement. */
export const createSiteIntelligenceRoutingFindingsMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceRoutingFindingListResponseToJSON(clone(routingFindings)) : null;
/** Returns the mock routing review-question catalog. */
export const createSiteIntelligenceRoutingReviewQuestionsMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceRoutingReviewQuestionListResponseToJSON(clone(routingReviewQuestions)) : null;
/** Resolves one opaque mock routing finding to its typed deterministic evidence. */
export const createSiteIntelligenceRoutingFindingMock = (siteId: string, findingId: string) => (supportedSite(siteId) && routingDetails[findingId] ? SiteIntelligenceRoutingFindingDetailResponseToJSON(clone(routingDetails[findingId])) : null);
/** Returns the bounded public topology overview for the mock snapshot. */
export const createSiteIntelligenceTopologyMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceTopologyResponseToJSON(clone(topology)) : null;
/** Returns the paginated public topology findings for the mock snapshot. */
export const createSiteIntelligenceTopologyFindingsMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceTopologyFindingListResponseToJSON(clone(topologyFindings)) : null;
/** Returns the paginated topology review-question catalog for the mock snapshot. */
export const createSiteIntelligenceTopologyReviewQuestionsMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceTopologyReviewQuestionListResponseToJSON(clone(topologyReviewQuestions)) : null;
/** Resolves one opaque public topology finding to typed structural evidence. */
export const createSiteIntelligenceTopologyFindingMock = (siteId: string, findingId: string) => (supportedSite(siteId) && topologyDetails[findingId] ? SiteIntelligenceTopologyFindingDetailResponseToJSON(clone(topologyDetails[findingId])) : null);
/** Returns the paginated public hostname-family list for the mock snapshot. */
export const createSiteIntelligenceNamingMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceNamingListResponseToJSON(clone(naming)) : null;
/** Returns the paginated naming review-question catalog for the mock snapshot. */
export const createSiteIntelligenceNamingReviewQuestionsMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceNamingReviewQuestionListResponseToJSON(clone(namingReviewQuestions)) : null;
/** Resolves one opaque public naming finding to its observed family evidence. */
export const createSiteIntelligenceNamingFindingMock = (siteId: string, findingId: string) => (supportedSite(siteId) && namingDetails[findingId] ? SiteIntelligenceNamingFindingDetailResponseToJSON(clone(namingDetails[findingId])) : null);
/** Returns the paginated compact observed VLAN catalog for the mock snapshot. */
export const createSiteIntelligenceVlansMock = (siteId: string) => supportedSite(siteId) ? SiteIntelligenceVlanListResponseToJSON(clone(vlans)) : null;
/** Resolves one observed VLAN to its current names, members, and interpretations. */
export const createSiteIntelligenceVlanMock = (siteId: string, vlanId: number) => (supportedSite(siteId) && vlanDetails[vlanId] ? SiteIntelligenceVlanDetailResponseToJSON(clone(vlanDetails[vlanId])) : null);
